import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Trophy, Users, Calendar, Shield, Star, TrendingUp, Heart, Zap } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';

export const ValuePropositionSection = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  const benefits = [
    {
      icon: Trophy,
      title: 'Lifetime Football Pathway',
      description: "Only club in Singapore offering youth to senior football. Players can progress through Academy to adult teams.",
      highlight: true
    },
    {
      icon: Star,
      title: 'SFL Division 1 Champions',
      description: 'Our senior team are current Singapore Football League Division 1 champions, a pathway to excellence.',
      highlight: false
    },
    {
      icon: TrendingUp,
      title: 'Progressive Discount Structure',
      description: 'The more sessions you commit to, the less you pay per session. Rewards dedicated players.',
      highlight: true
    },
    {
      icon: Users,
      title: '234+ Football Section Members',
      description: 'Join a vibrant community spanning ages 16 to 76, with social, competitive, and ladies teams.',
      highlight: false
    },
    {
      icon: Calendar,
      title: '1.5 Hour Sessions',
      description: 'Longer sessions than many competitors who offer 60 minute training, maximizing development time.',
      highlight: false
    },
    {
      icon: Shield,
      title: 'No GST (Direct Payment)',
      description: 'Save on GST when paying directly to Football And More Limited instead of via SCC account.',
      highlight: true
    },
    {
      icon: Heart,
      title: 'Family Discounts',
      description: '20% off for 2 children, 25% off for 3 children. Making football accessible for families.',
      highlight: false
    },
    {
      icon: Zap,
      title: 'Digital Membership Option',
      description: 'Non members can access 5% member discount and future benefits through our Digital Membership.',
      highlight: false
    },
  ];

  const uniquePoints = [
    {
      stat: 'Only',
      label: 'Club with lifetime pathway',
      description: 'Youth to Adult football progression'
    },
    {
      stat: '24%',
      label: 'Savings for committed players',
      description: '3x/week vs 1x/week per session rate'
    },
    {
      stat: '0%',
      label: 'GST on direct payments',
      description: 'Pay Football And More Limited directly'
    },
    {
      stat: '172',
      label: 'Years of heritage',
      description: "Established 1852, Singapore's premier club"
    },
  ];

  return (
    <section id="value" className="min-h-screen py-24 px-6">
      <div className="max-w-6xl mx-auto">
        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          {/* Section Header */}
          <motion.div variants={itemVariants} className="text-center mb-16">
            <Badge variant="outline" className="gold-border text-primary mb-4">
              Why Choose Us
            </Badge>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-gradient-gold mb-4">
              The SCC Advantage
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto text-base">
              More than just an academy, a complete football journey
            </p>
          </motion.div>

          {/* Unique Stats */}
          <motion.div variants={itemVariants} className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-16">
            {uniquePoints.map((point, idx) => (
              <div key={idx} className="glass-card gold-border rounded-xl p-6 text-center">
                <p className="font-display text-3xl lg:text-4xl font-bold text-primary mb-1">{point.stat}</p>
                <p className="text-card-foreground font-medium text-sm mb-2">{point.label}</p>
                <p className="text-muted-foreground text-xs">{point.description}</p>
              </div>
            ))}
          </motion.div>

          {/* Benefits Grid */}
          <motion.div variants={itemVariants}>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {benefits.map((benefit, idx) => {
                const Icon = benefit.icon;
                return (
                  <motion.div
                    key={idx}
                    variants={itemVariants}
                    whileHover={{ y: -5 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Card className={`bg-gradient-card gold-border h-full ${
                      benefit.highlight ? 'ring-1 ring-primary/50' : ''
                    }`}>
                      <CardContent className="p-6">
                        <div className={`inline-flex p-3 rounded-lg mb-4 ${
                          benefit.highlight ? 'bg-primary/20' : 'bg-charcoal'
                        }`}>
                          <Icon className={`h-6 w-6 ${benefit.highlight ? 'text-primary' : 'text-muted-foreground'}`} />
                        </div>
                        <h4 className="font-display text-lg text-card-foreground mb-2">{benefit.title}</h4>
                        <p className="text-muted-foreground text-sm leading-relaxed">{benefit.description}</p>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>

          {/* SCC Heritage */}
          <motion.div variants={itemVariants} className="mt-16">
            <Card className="bg-gradient-card gold-border overflow-hidden">
              <CardContent className="p-8">
                <div className="flex flex-col lg:flex-row items-center gap-8">
                  <div className="flex-shrink-0">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_fc9d91ec-20e9-46cd-af6f-ead15b7d04e2/artifacts/aklhfw0k_SCC%20LOGO.jpeg" 
                      alt="SCC Logo" 
                      className="h-32 w-32 object-contain"
                    />
                  </div>
                  <div>
                    <h3 className="font-display text-2xl text-primary mb-4">Singapore Cricket Club, Established 1852</h3>
                    <p className="text-muted-foreground leading-relaxed mb-4">
                      For over 172 years, the Singapore Cricket Club has been at the heart of Singapore's sporting community. 
                      Our Football Section continues this legacy of excellence, now offering a unique pathway from youth 
                      development through to senior competitive football.
                    </p>
                    <p className="text-muted-foreground leading-relaxed">
                      With our SFL Division 1 team as current champions, young players have a clear vision of what they 
                      can achieve, all within the same club family.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default ValuePropositionSection;
